import { useState, useEffect } from "react";
import { ScrollView, View, Text, Pressable } from "react-native";
import { useRouter } from "expo-router";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useClub } from "@/contexts/ClubContext";
import { deleteTrainingSeries } from "@/lib/services";
import { useConfirm } from "@/hooks/useConfirm";
import ConfirmSheet from "@/components/ConfirmSheet";
import { colors, radius } from "@/lib/theme";
import { PencilIcon, TrashIcon } from "@/components/Icons";
import ContentWidth from "@/components/ContentWidth";
import type { TrainingSeries } from "@/lib/types";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export default function TrainingSeriesScreen() {
  const router = useRouter();
  const { club } = useClub();
  const { confirmProps, confirm } = useConfirm();
  const [series, setSeries] = useState<(TrainingSeries & { id: string })[]>([]);

  useEffect(() => {
    if (!club?.id) return;
    const unsub = onSnapshot(
      query(collection(db, "clubs", club.id, "trainingSeries"), orderBy("createdAt", "desc")),
      (snap) => setSeries(snap.docs.map((d) => ({ id: d.id, ...(d.data() as TrainingSeries) })))
    );
    return unsub;
  }, [club?.id]);

  const handleDelete = async (s: TrainingSeries & { id: string }) => {
    if (!club) return;
    const ok = await confirm({
      title: `Delete "${s.title}"?`,
      message: "All future sessions will be deleted. Past sessions are preserved.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await deleteTrainingSeries(club.id, s.id, "all");
  };

  return (
    <ScrollView style={{ flex: 1, backgroundColor: colors.bg }}>
      <ContentWidth>
        <View style={{ padding: 20 }}>
          <Pressable
            onPress={() => router.push("/(app)/create-training")}
            style={({ pressed }) => ({
              backgroundColor: colors.brand,
              borderRadius: radius.md,
              padding: 14,
              alignItems: "center",
              marginBottom: 20,
              opacity: pressed ? 0.8 : 1,
            })}
          >
            <Text style={{ color: "#fff", fontSize: 14, fontWeight: "700" }}>+ New training series</Text>
          </Pressable>

          {series.length === 0 && (
            <View style={{ padding: 40, alignItems: "center" }}>
              <Text style={{ fontSize: 36, marginBottom: 12 }}>🏋️</Text>
              <Text style={{ fontSize: 14, color: colors.inkMuted, textAlign: "center" }}>No training series yet</Text>
            </View>
          )}

          {series.map((s) => (
            <View key={s.id} style={{ backgroundColor: colors.white, borderRadius: radius.lg, borderWidth: 1, borderColor: colors.line, padding: 16, marginBottom: 12 }}>
              <Text style={{ fontSize: 17, fontWeight: "800", color: colors.ink }}>{s.title}</Text>
              <Text style={{ fontSize: 13, color: colors.inkMuted, marginTop: 4 }}>
                {DAYS[s.dayOfWeek]}s · {s.time} · {s.duration} min
              </Text>
              <Text style={{ fontSize: 13, color: colors.inkMuted, marginTop: 2 }}>{s.location}</Text>
              <Text style={{ fontSize: 13, color: colors.inkMuted, marginTop: 2 }}>
                {s.startDate} → {s.endDate}{s.fee > 0 ? ` · £${(s.fee / 100).toFixed(2)}/session` : " · Free"}
              </Text>
              <View style={{ flexDirection: "row", gap: 10, marginTop: 12 }}>
                <Pressable
                  onPress={() => router.push(`/(app)/edit-training?seriesId=${s.id}&title=${encodeURIComponent(s.title)}&time=${s.time}&duration=${s.duration}&location=${encodeURIComponent(s.location)}&fee=${s.fee}&dayOfWeek=${s.dayOfWeek}&startDate=${s.startDate}&endDate=${s.endDate}`)}
                  style={({ pressed }) => ({
                    width: 40, height: 40, borderRadius: 20,
                    backgroundColor: colors.brand50, borderWidth: 1, borderColor: "#86efac",
                    alignItems: "center", justifyContent: "center",
                    opacity: pressed ? 0.7 : 1,
                  })}
                >
                  <PencilIcon size={18} color={colors.brandDark} />
                </Pressable>
                <Pressable
                  onPress={() => handleDelete(s)}
                  style={({ pressed }) => ({
                    width: 40, height: 40, borderRadius: 20,
                    backgroundColor: colors.dangerLight, borderWidth: 1, borderColor: "#fecaca",
                    alignItems: "center", justifyContent: "center",
                    opacity: pressed ? 0.7 : 1,
                  })}
                >
                  <TrashIcon size={18} color={colors.danger} />
                </Pressable>
              </View>
            </View>
          ))}
        </View>
      </ContentWidth>
      <ConfirmSheet {...confirmProps} />
    </ScrollView>
  );
}
